import { sortBy } from 'es-toolkit';
import type { Rect } from 'react-use-rect';
import type { SourceId } from '../../../constants';
import { SourceTypes } from '../../../constants';
import type { LayoutType } from '../../../types';
import type {
  GalleryMediaValue,
  ImageMediaValue,
  MediaValue,
  PictureValueType
} from '../../data/sources';

const DEFAULT_TUMBLR_PIXEL_RATIO = 1.5;

const fallbackMedia: ImageMediaValue = {
  type: 'image',
  src: ''
};

const getDevicePixelRatio = (platform: SourceId) => {
  const devicePixelRatio = Math.trunc(window.devicePixelRatio) || 1;

  if (platform === SourceTypes.TUMBLR) {
    return Math.max(devicePixelRatio, DEFAULT_TUMBLR_PIXEL_RATIO);
  }

  return devicePixelRatio;
};

export const optimizeImagesSize = (
  media: MediaValue<string | PictureValueType[]> | undefined,
  platform: SourceId,
  rect: Rect,
  imageRatio: number,
  layout: LayoutType,
  horizontal: boolean
): MediaValue => {
  if (!media) {
    return fallbackMedia;
  }

  const getOptimizedImageURL = (arrayImages: PictureValueType[]) => {
    const sortedImages = sortBy(arrayImages, ['width']);
    const devicePixelRatio = getDevicePixelRatio(platform);
    const cellWidth = horizontal
      ? Math.ceil(rect.height / imageRatio)
      : rect.width;

    const ratioAvailable =
      layout === 'grid' || layout === 'carousel' || horizontal;
    const postWidth = cellWidth * devicePixelRatio;

    const fitImage =
      sortedImages.find((photo) =>
        ratioAvailable
          ? photo.width >= postWidth && photo.height >= postWidth * imageRatio
          : photo.width >= postWidth
      ) ?? sortedImages[sortedImages.length - 1];

    return fitImage.url;
  };

  const optimizedMedia = { ...media } as MediaValue;

  if (media.src && Array.isArray(media.src) && media.src.length) {
    optimizedMedia.src = getOptimizedImageURL(media.src);
  }

  if (
    media.thumbnail &&
    Array.isArray(media.thumbnail) &&
    media.thumbnail.length
  ) {
    optimizedMedia.thumbnail = getOptimizedImageURL(media.thumbnail);
  }

  if (media.items) {
    optimizedMedia.items = media.items.map((item) =>
      optimizeImagesSize(item, platform, rect, imageRatio, layout, horizontal)
    ) as GalleryMediaValue['items'];
  }

  return optimizedMedia;
};
